import React from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
} from "@mui/material";
import {
  People as PeopleIcon,
  Class as ClassIcon,
  CheckCircle as CheckCircleIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import StatCard from "../components/infographics/StatCard";
import { useAuth } from "../contexts/AuthContext";

const TLDashboard = () => {
  const theme = useTheme();
  const { user } = useAuth();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <Box>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box sx={{ mb: 4, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                Team Leader Dashboard
              </Typography>
              <Typography variant="body1" sx={{ color: "text.secondary" }}>
                Welcome back, {user?.name || user?.user_name || "Team Leader"}! Manage your team and classes.
              </Typography>
            </Box>
            <Chip
              label="Team Leader"
              color="primary"
              sx={{ fontWeight: 600 }}
            />
          </Box>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="My Students"
                value={156}
                icon={<PeopleIcon />}
                color="primary"
                trend="+8% this month"
                trendColor="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="My Classes"
                value={12}
                icon={<ClassIcon />}
                color="secondary"
                trend="+2 this month"
                trendColor="success"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Attendance Rate"
                value={94.5}
                icon={<CheckCircleIcon />}
                color="success"
                trend="+2.1% this week"
                trendColor="success"
                suffix="%"
                decimals={1}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard
                title="Assignments"
                value={24}
                icon={<AssignmentIcon />}
                color="warning"
                trend="5 pending"
                trendColor="warning"
              />
            </Grid>
          </Grid>
        </motion.div>

        {/* Team Overview */}
        <motion.div variants={itemVariants}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Team Performance Overview
                  </Typography>
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <TrendingUpIcon sx={{ fontSize: 64, color: "primary.main", mb: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      Overall Team Performance
                    </Typography>
                    <Typography variant="body1" sx={{ color: "text.secondary" }}>
                      Track your team's progress and performance metrics
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Quick Actions
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {[
                      { label: "Mark Attendance", icon: <CheckCircleIcon />, color: "success" },
                      { label: "View Classes", icon: <ClassIcon />, color: "primary" },
                      { label: "Manage Students", icon: <PeopleIcon />, color: "secondary" },
                      { label: "Review Assignments", icon: <AssignmentIcon />, color: "warning" },
                    ].map((action, index) => (
                      <motion.div
                        key={index}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Card
                          sx={{
                            p: 2,
                            cursor: "pointer",
                            border: `1px solid ${theme.palette.divider}`,
                            "&:hover": {
                              borderColor: `${action.color}.main`,
                              bgcolor: `${action.color}.light` + "10",
                            },
                          }}
                        >
                          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                            <Box
                              sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: `${action.color}.light`,
                                color: `${action.color}.main`,
                              }}
                            >
                              {action.icon}
                            </Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {action.label}
                            </Typography>
                          </Box>
                        </Card>
                      </motion.div>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </motion.div>
      </motion.div>
    </Box>
  );
};

export default TLDashboard;

